import {
	Row,
	Col,
	Button,
	Form,
	Input,
	Space,
	Typography,
	Steps,
	Spin
} from 'antd'
import {
	LoadingOutlined,
	Loading3QuartersOutlined,
	SolutionOutlined,
	UserOutlined
} from '@ant-design/icons'

import { observer } from 'mobx-react-lite'
import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import {
	resetPassword,
	resetPasswordConfirmation
} from '../../../api/modules/api-user'
import { GeneralContentWrapper } from '../../../components/Shared/GeneralComponents'
import { useStores } from '../../../cls/stores/MobXProvider'
import { startError } from '../../../stores/work-progress/actions'
import { useSmartTranslation } from '../../../cls/TranslationContext'
const { Title, Text } = Typography // Import Typography for text styling

/**
 * ResetPassword
 *
 * A React component that provides a form for resetting a user's password.
 * If the user is logged in, their email will be prefilled and non-editable.
 * Upon successful password reset, the user is redirected to the login page.
 *
 * @component
 * @returns {JSX.Element} The rendered ResetPassword form.
 */
const ResetPassword = ({ actionOnLogin, hideRelated }) => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const { users } = useStores()
	const { loggedInUser } = users
	const [formData, setFormData] = React.useState(null)
	const [step, setStep] = React.useState(1)
	const [sendingEmail, setSendingEmail] = React.useState(false)
	// Disable the email field if the user is logged in and has an email
	const isEmailFieldDisabled = loggedInUser?.email ? true : false

	// Initial form values, using the logged-in user's email if available
	const _initialFormValues = {
		email: loggedInUser?.email || '',
		password: ''
	}

	const [form] = Form.useForm()

	const onFinishStep1 = async (values) => {
		setFormData({
			email: values.email,
			password: values.password
		})
		setSendingEmail(true)
		resetPasswordConfirmation(values.email).then((response) => {
			setSendingEmail(false)
			if (response.status !== 'success') {
				startError(response.message)
			}
		})
		setStep(2)
	}

	/**
	 * onFinish
	 *
	 * Handles form submission. Sends a request to reset the user's password using the email and password provided.
	 * If the reset is successful, it navigates to the login page. Otherwise, it triggers an error notification.
	 *
	 * @param {Object} values - Form values containing email, password, and password confirmation.
	 * @returns {Promise<void>}
	 */
	const onFinishStep2 = async (values) => {
		try {
			resetPassword({
				email: formData.email,
				password: formData.password,
				confirmation_code: values.confirmation_code
			})
				.then((response) => {
					if (response.status === 'success') {
						alert(t('single.password.reset-success'))
						if (typeof actionOnLogin === 'function') {
							actionOnLogin()
							return
						}
						if (users.isLoggedIn) {
							navigate('/dashboard')
						} else {
							navigate('/login')
						}
					} else {
						startError(response.message)
					}
				})
				.catch((error) => {
					console.error('Failed to reset user password:', error)
				})
		} catch (error) {
			alert('Password reset failed, please try again later.')
			console.error('Reset password error:', error)
		}
	}

	return (
		<GeneralContentWrapper>
			<Space
				direction="vertical"
				style={{ width: '100%', marginBottom: '20px' }}
			>
				<Title level={2}>{t('single.password.reset')}</Title>
			</Space>

			<Steps
				items={[
					{
						title: 'New Password',
						status: step == 1 ? 'process' : 'finish',
						icon: <SolutionOutlined />
					},
					{
						title: 'Email confirmation code',
						status: step == 2 ? 'process' : 'wait',
						icon:
							step == 2 ? (
								<LoadingOutlined />
							) : (
								<Loading3QuartersOutlined />
							)
					},
					{
						title: 'Login',
						status: step == 3 ? 'process' : 'wait',
						icon: <UserOutlined />
					}
				]}
			/>
			<br></br>
			{step == 1 && (
				<Form
					form={form}
					name="reset-password"
					onFinish={onFinishStep1}
					layout="vertical"
					initialValues={_initialFormValues}
				>
					<Form.Item
						name="email"
						label={t('single.email')}
						rules={[
							{
								required: true,
								message: t('__provide.please%single.email'),
								type: 'email'
							}
						]}
					>
						<Input
							placeholder={t('__provide.please%single.email')}
							disabled={isEmailFieldDisabled}
						/>
					</Form.Item>
					<Row gutter={16}>
						<Col xs={24} sm={12}>
							<Form.Item
								name="password"
								label={t('single.password.label')}
								rules={[
									{
										required: true,
										message: t(
											'__provide.please%single.password.label'
										)
									}
								]}
							>
								<Input.Password
									placeholder={t('single.password.label')}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item
								name="password_match"
								label={t('single.password.match')}
								rules={[
									{
										required: true,
										message: t(
											'__provide.please%single.password.label'
										),
										validator: (_, value) => {
											if (
												!value ||
												form.getFieldValue(
													'password'
												) === value
											) {
												return Promise.resolve()
											}
											return Promise.reject(
												new Error(
													t(
														'single.password.not-match'
													)
												)
											)
										}
									}
								]}
							>
								<Input.Password
									placeholder={t('single.password.label')}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Form.Item>
						<Button type="primary" htmlType="submit">
							{t('single.password.reset')}
						</Button>
					</Form.Item>
				</Form>
			)}
			{step == 2 && sendingEmail && (
				<div style={{ textAlign: 'center' }}>
					<p>{t('single.sending-email')}</p>
					<Spin />
				</div>
			)}
			{step == 2 && !sendingEmail && (
				<Form
					name="reset-password"
					onFinish={onFinishStep2}
					layout="vertical"
				>
					<p style={{ color: 'green' }}>
						{t('single.confirmation.code-mail')}
					</p>
					<Form.Item
						name="confirmation_code"
						label={t('single.confirmation.code')}
						rules={[
							{
								required: true,
								message: t(
									'__provide.please%single.confirmation.code'
								)
							}
						]}
					>
						<Input
							placeholder={t(
								'__provide.please%single.confirmation.code'
							)}
						/>
					</Form.Item>
					<Form.Item>
						<Button type="primary" htmlType="submit">
							{t('single.confirm')}
						</Button>
					</Form.Item>
				</Form>
			)}

			{!hideRelated && (
				<Text style={{ display: 'block', textAlign: 'center' }}>
					{t('single.login.existing-account')}{' '}
					<Link to="../login">{t('single.login.now')}</Link>
				</Text>
			)}
		</GeneralContentWrapper>
	)
}

ResetPassword.propTypes = {
	actionOnLogin: PropTypes.func,
	hideRelated: PropTypes.bool
}

export default observer(ResetPassword)
